import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Bookmark, Check, Lock, MoreHorizontal } from 'lucide-react';
import { toast } from 'react-toastify';
import { HUB_SIZE_CAP } from '../../lib/basketSearch';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { isLocked } from '../../lib/premiumLock';
import { deleteBasket, updateBasket } from '../../store/workspacesSlice';
import { BasketIcon } from '../BasketIcon';
import EditBasketModal from '../WorkspaceDetail/EditBasketModal';
import { usePremium } from '../Subscription/PremiumProvider';
import { highlightMatch } from './highlightMatch';
import { pickStartTopicId } from './basketRow';
import BasketHubGraph from './BasketHubGraph';
import TopicInspectPanel from './TopicInspectPanel';
import ConfirmDialog from './ConfirmDialog';

const ROUND_DONE_MS = 1600;

const formatItemCount = (count) =>
  `${count} item${count === 1 ? '' : 's'}`;

const isEligibleRecallAdvanceTopic = (topic, { isPremium, isLoading }) => {
  if (!topic || topic.is_anchor) return false;
  if (isLocked(topic, isPremium, isLoading)) return false;
  return true;
};

const findNextRecallTopic = (topics, fromTopicId, opts) => {
  const fromIndex = topics.findIndex((topic) => topic.id === fromTopicId);
  if (fromIndex < 0) return null;
  for (let i = fromIndex + 1; i < topics.length; i += 1) {
    if (isEligibleRecallAdvanceTopic(topics[i], opts)) return topics[i];
  }
  return null;
};

/**
 * Expanded basket search/default result: header + hub (or pill list if oversized) + inspect.
 */
const ConnectionsExpandedBasketCard = ({
  item,
  searchTerm = '',
  onCollapse,
  liveBasket,
  workspaceId,
  onTogglePin,
  onMoveToBasket,
  onTopicDeleted,
  onBeforeLeave,
  onAddItemToBasket,
  headerCollapsible = true,
  onNextBasket,
}) => {
  const dispatch = useDispatch();
  const { isPremium, isLoading } = usePremium();
  const topics = useMemo(
    () => liveBasket?.topics || item.topics || [],
    [liveBasket?.topics, item.topics]
  );

  const basket = liveBasket || {
    id: item.id,
    name: item.name,
    topics,
    is_locked: item.is_locked,
  };

  const isPinned = Boolean(liveBasket?.is_pinned ?? basket.is_pinned);
  const showBasketLock = isLocked(basket, isPremium, isLoading);
  const topicIdsKey = topics.map((t) => t.id).join(',');

  const [selectedTopicId, setSelectedTopicId] = useState(
    () => item.matchedTopicId || topics[0]?.id || null
  );
  const [roundFinished, setRoundFinished] = useState(false);
  const [restartCount, setRestartCount] = useState(0);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const isSavingEditRef = useRef(false);
  const roundDoneTimerRef = useRef(null);
  const onNextBasketRef = useRef(onNextBasket);
  onNextBasketRef.current = onNextBasket;

  useEffect(() => {
    setSelectedTopicId((prev) => {
      if (prev && topics.some((t) => t.id === prev)) return prev;
      if (
        item.matchedTopicId &&
        topics.some((t) => t.id === item.matchedTopicId)
      ) {
        return item.matchedTopicId;
      }
      return topics[0]?.id || null;
    });
    // topicIdsKey captures identity of the topic list without depending on array ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.matchedTopicId, topicIdsKey]);

  // Only reset on basket change — topic ripe updates must not cancel the overlay.
  useEffect(() => {
    setRoundFinished(false);
    if (roundDoneTimerRef.current) {
      clearTimeout(roundDoneTimerRef.current);
      roundDoneTimerRef.current = null;
    }
  }, [item.id]);

  // Done overlay, then next basket (or restart this one when no next).
  useEffect(() => {
    if (!roundFinished) return undefined;

    roundDoneTimerRef.current = setTimeout(() => {
      roundDoneTimerRef.current = null;
      if (onNextBasketRef.current) {
        onNextBasketRef.current();
        return;
      }
      setRoundFinished(false);
      setSelectedTopicId(pickStartTopicId(basket));
      setRestartCount((count) => count + 1);
    }, ROUND_DONE_MS);

    return () => {
      if (roundDoneTimerRef.current) {
        clearTimeout(roundDoneTimerRef.current);
        roundDoneTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundFinished, basket.id]);

  const selectedTopic = useMemo(
    () => topics.find((t) => t.id === selectedTopicId) || null,
    [topics, selectedTopicId]
  );

  const ripeCount = topics.filter((t) => t.is_ripe).length;

  const oversized = item.oversized ?? topics.length > HUB_SIZE_CAP;
  const trimmedSearch = searchTerm?.trim() || '';
  // Golden match ring only while searching — not for default/row start topics.
  const highlightMatchId = trimmedSearch ? item.matchedTopicId : null;

  const handleSelectTopic = (topic) => {
    const isMatch = Boolean(
      highlightMatchId && topic.id === highlightMatchId
    );

    captureConnectionsEvent('connections_spoke_opened', {
      topic_id: topic.id,
      basket_id: basket.id,
      is_match: isMatch,
      is_ripe: Boolean(topic.is_ripe),
    });

    // Aha: active search, opened a connected topic that wasn't the search match
    if (
      trimmedSearch &&
      item.matchedTopicId &&
      topic.id !== item.matchedTopicId
    ) {
      captureConnectionsEvent('connection_discovered', {
        workspace_id: workspaceId,
        basket_id: basket.id,
        matched_topic_id: item.matchedTopicId,
        discovered_topic_id: topic.id,
        search_term: trimmedSearch,
      });
    }

    setRoundFinished(false);
    setSelectedTopicId(topic.id);
  };

  const handleRecallComplete = () => {
    if (!selectedTopicId || !basket?.id) return;

    const nextTopic = findNextRecallTopic(topics, selectedTopicId, {
      isPremium,
      isLoading,
    });

    if (nextTopic) {
      captureConnectionsEvent('recall_advanced', {
        basket_id: basket.id,
        from_topic_id: selectedTopicId,
        to_topic_id: nextTopic.id,
      });
      setSelectedTopicId(nextTopic.id);
      return;
    }

    captureConnectionsEvent('basket_round_finished', {
      basket_id: basket.id,
      topic_count: topics.length,
    });
    setRoundFinished(true);
  };

  const handleTogglePin = (event) => {
    event.stopPropagation();
    onTogglePin?.(liveBasket || basket);
  };

  const handleOpenEditBasket = (event) => {
    event?.stopPropagation();
    setIsEditOpen(true);
  };

  const handleCloseEditBasket = () => {
    if (isSavingEditRef.current) return;
    setIsEditOpen(false);
  };

  const handleSaveBasket = async ({ name }) => {
    if (!workspaceId || !basket?.id || isSavingEditRef.current) return;

    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      toast.error('Basket name cannot be empty.');
      return;
    }

    const currentName = basket.name || '';
    if (trimmedName === currentName) {
      setIsEditOpen(false);
      return;
    }

    isSavingEditRef.current = true;
    try {
      await dispatch(
        updateBasket({
          basketId: basket.id,
          updatedData: { name: trimmedName },
          workspaceId,
        })
      ).unwrap();

      if (trimmedName !== currentName) {
        captureConnectionsEvent('basket_renamed', {
          basket_id: basket.id,
          workspace_id: workspaceId,
          source: 'menu',
        });
      }

      toast.success('Basket updated');
      setIsEditOpen(false);
    } catch (error) {
      console.error('Error updating basket:', error);
      toast.error('Could not update the basket. Please try again.');
    } finally {
      isSavingEditRef.current = false;
    }
  };

  const handleOpenDeleteBasket = (event) => {
    event?.stopPropagation();
    setIsDeleteOpen(true);
  };

  const handleAddItemFromMenu = (event) => {
    event?.stopPropagation();
    onAddItemToBasket?.(basket, 'menu');
  };

  const handleAddFirstItem = () => {
    onAddItemToBasket?.(basket, 'empty_state');
  };

  const isEmpty = topics.length === 0;
  const deleteDialogBody = isEmpty
    ? 'This basket is empty. Only the basket is removed.'
    : `Its ${formatItemCount(topics.length)} stay in your workspace as unfiled items. Only the basket is removed.`;

  const handleCancelDeleteBasket = () => {
    if (isDeleting) return;
    setIsDeleteOpen(false);
  };

  const handleConfirmDeleteBasket = async () => {
    if (!workspaceId || !basket?.id || isDeleting) return;
    setIsDeleting(true);
    const topicCount = topics.length;
    try {
      await dispatch(
        deleteBasket({ workspaceId, basketId: basket.id })
      ).unwrap();
      captureConnectionsEvent('basket_deleted', {
        basket_id: basket.id,
        workspace_id: workspaceId,
        topic_count: topicCount,
      });
      toast.success('Basket deleted');
      setIsDeleteOpen(false);
      onCollapse?.();
    } catch (error) {
      console.error('Error deleting basket:', error);
      toast.error('Could not delete the basket. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTopicDeleted = (topic) => {
    onTopicDeleted?.(topic);
  };

  const hasNextRecall = Boolean(
    selectedTopicId &&
      findNextRecallTopic(topics, selectedTopicId, { isPremium, isLoading })
  );

  const inspectPanel = (
    <TopicInspectPanel
      key={`${selectedTopicId}-${restartCount}`}
      topic={
        selectedTopic
          ? { ...selectedTopic, basket: selectedTopic.basket || basket }
          : null
      }
      onMoveToBasket={onMoveToBasket}
      onDeleted={handleTopicDeleted}
      onBeforeLeave={onBeforeLeave}
      onRecallComplete={handleRecallComplete}
      hasNextRecall={hasNextRecall}
    />
  );

  const nameBlock = (
    <>
      <span className="text-xl font-semibold text-neutral-900 truncate">
        {highlightMatch(basket.name, searchTerm)}
      </span>
      {showBasketLock && (
        <span className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 bg-neutral-100 border border-neutral-300 rounded-full px-1.5 py-0.5">
          <Lock size={10} aria-hidden="true" />
          Premium
        </span>
      )}
    </>
  );

  return (
    <div className="relative border-2 border-neutral-200 rounded-xl bg-white overflow-hidden">
      {roundFinished && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-white basket-round-done-overlay"
          role="status"
          aria-live="polite"
        >
          <div className="flex flex-col items-center gap-2 px-4 text-center pointer-events-none">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-600 text-white shadow-md">
              <Check size={32} strokeWidth={3} aria-hidden="true" />
            </span>
            <p className="text-base font-semibold text-neutral-900">
              Basket done
            </p>
          </div>
        </div>
      )}
      <div className="group/header relative border-b border-neutral-200">
        <div className="w-full text-center px-4 py-3 pr-20">
          <div className="flex items-center justify-center gap-3 min-w-0 max-w-full">
            {headerCollapsible ? (
              <button
                type="button"
                onClick={onCollapse}
                className="inline-flex items-center gap-2 min-w-0 max-w-[85%] text-left hover:opacity-80 focus:outline-none rounded"
                aria-label={`Collapse ${basket.name}`}
              >
                {nameBlock}
              </button>
            ) : (
              <div className="inline-flex items-center gap-2 min-w-0 max-w-[85%]">
                {nameBlock}
              </div>
            )}
          </div>
          {headerCollapsible && (
            <p className="sr-only">Collapse</p>
          )}
        </div>
        <div className="absolute right-2 top-2 z-10 flex items-center gap-0.5">
          <button
            type="button"
            onClick={handleTogglePin}
            className={`shrink-0 p-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700 ${
              isPinned
                ? 'text-butter-500 hover:text-butter-600 hover:bg-neutral-200'
                : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200'
            }`}
            aria-label={isPinned ? `Unpin ${basket.name}` : `Pin ${basket.name}`}
            aria-pressed={isPinned}
          >
            <Bookmark
              size={20}
              fill={isPinned ? 'currentColor' : 'none'}
              aria-hidden="true"
            />
          </button>
          <Menu as="div" className="relative">
            <MenuButton
              className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700"
              aria-label={`More actions for ${basket.name}`}
              aria-haspopup="menu"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </MenuButton>
            <MenuItems
              anchor="bottom end"
              className="z-50 w-56 origin-top-right rounded-xl border-2 border-neutral-900 bg-white py-1 shadow-lg focus:outline-none"
            >
              {!isEmpty && (
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={handleAddItemFromMenu}
                      className={`block w-full px-3 py-2 text-left text-sm text-neutral-800 ${
                        focus ? 'bg-neutral-100' : ''
                      }`}
                    >
                      Add item to this basket
                    </button>
                  )}
                </MenuItem>
              )}
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={handleOpenEditBasket}
                    className={`block w-full px-3 py-2 text-left text-sm text-neutral-800 ${
                      focus ? 'bg-neutral-100' : ''
                    }`}
                  >
                    Edit basket
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={handleOpenDeleteBasket}
                    className={`block w-full px-3 py-2 text-left text-sm ${
                      focus ? 'bg-neutral-100' : ''
                    } text-cranberry-700`}
                  >
                    Delete basket
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      </div>

      <div className="p-4 sm:p-5 space-y-4">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center text-center py-8 px-2 sm:px-4 space-y-3">
            <span
              className="opacity-40 scale-125 [&_svg]:mr-0 [&_svg]:text-neutral-400"
              aria-hidden="true"
            >
              <BasketIcon />
            </span>
            <h3 className="text-base font-semibold text-neutral-900">
              This basket is empty
            </h3>
            <p className="text-sm text-neutral-500 max-w-sm">
              Baskets work best with items from different subjects.
            </p>
            <button
              type="button"
              onClick={handleAddFirstItem}
              className="mt-1 px-5 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cranberry-700"
            >
              Add the first item
            </button>
            <button
              type="button"
              onClick={handleOpenDeleteBasket}
              className="text-sm text-neutral-500 underline hover:text-cranberry-700 focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded"
            >
              Delete basket
            </button>
          </div>
        ) : !oversized ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="order-2 flex min-h-[200px] h-full flex-col border border-neutral-100 rounded-lg sm:order-1">
              {inspectPanel}
            </div>
            <div className="order-1 sm:order-2">
              <BasketHubGraph
                basket={basket}
                selectedTopicId={selectedTopicId}
                matchedTopicId={highlightMatchId}
                onSelectTopic={handleSelectTopic}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="group/hub relative pt-5">
              <p
                className="absolute top-0 left-0 z-10 text-xs text-neutral-500 transition-opacity opacity-100 md:opacity-0 md:group-hover/hub:opacity-100 md:group-focus-within/hub:opacity-100"
                aria-label={`${ripeCount} of ${topics.length} ripe`}
              >
                {isEmpty ? 'Empty' : `${ripeCount}/${topics.length} ripe`}
              </p>
              <div className="flex flex-wrap gap-2">
              {topics.map((topic) => {
                const isSelected = topic.id === selectedTopicId;
                const isMatch = Boolean(
                  highlightMatchId && topic.id === highlightMatchId
                );
                const topicLocked = isLocked(topic, isPremium, isLoading);
                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => handleSelectTopic(topic)}
                    className={`
                      inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border-2 transition-colors
                      focus:outline-none focus:ring-2 focus:ring-cranberry-700
                      ${isSelected
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : 'border-neutral-300 bg-white text-neutral-800 hover:border-neutral-500'}
                      ${isMatch && !isSelected ? 'ring-2 ring-offset-1 ring-[#C9982E]' : ''}
                    `}
                    aria-pressed={isSelected}
                  >
                    {topic.name}
                    {topicLocked && (
                      <Lock
                        size={12}
                        className={isSelected ? 'text-neutral-300' : 'text-neutral-400'}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
              </div>
            </div>
            <div className="flex min-h-[280px] flex-col border border-neutral-100 rounded-lg">
              {inspectPanel}
            </div>
          </>
        )}
      </div>

      {isEditOpen && (
        <EditBasketModal
          basket={basket}
          onClose={handleCloseEditBasket}
          onSave={handleSaveBasket}
        />
      )}

      <ConfirmDialog
        isOpen={isDeleteOpen}
        title={`Delete "${basket.name}"?`}
        body={deleteDialogBody}
        confirmLabel="Delete basket"
        isLoading={isDeleting}
        onConfirm={handleConfirmDeleteBasket}
        onCancel={handleCancelDeleteBasket}
      />
    </div>
  );
};

export default ConnectionsExpandedBasketCard;
